import React, { useState } from "react";
import { Mail, X } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { AppConfig } from "../../utils/Constants";

interface ForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
}

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const { theme } = useTheme();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setMessage(null);
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) {
      setMessage({
        type: "error",
        text: "Please enter a valid email address.",
      });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      await onSubmit(email);
      setMessage({
        type: "success",
        text: "Password reset email sent. Please check your inbox.",
      });
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "Failed to send password reset email.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
      <div className="bg-background-paper dark:bg-background-paperDark rounded-lg shadow-xl w-full max-w-md p-8 relative border border-divider dark:border-divider-dark animate-fadeIn">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors p-1 rounded-md hover:bg-background-panel dark:hover:bg-background-panelDark"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6 flex flex-col items-center">
          <div className="mb-4 p-3 rounded-full bg-secondary/10 dark:bg-secondary/20">
            <Mail className="w-8 h-8 text-secondary" />
          </div>
          <div className="flex items-center gap-2 mb-2">
            <img
              src={
                theme === "light"
                  ? "https://cdn.forgeworks.ai/assets/forgeworks-logo-black.png"
                  : AppConfig.LOGO_URL
              }
              alt={`${AppConfig.PRODUCT_NAME_SHORT} Logo`}
              className="w-5 h-5 object-contain"
            />
            <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
              {AppConfig.PRODUCT_NAME_SHORT}
            </span>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-center text-text-primary dark:text-text-primaryDark mb-2 tracking-tight">
          Reset Password
        </h2>

        <p className="text-sm text-center text-text-secondary dark:text-text-secondaryDark mb-6 leading-relaxed">
          Enter your email address below. If an account exists, we'll send you
          instructions to reset your password.
        </p>

        <div className="mb-4">
          <label className="block text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
            Email
          </label>
          <input
            type="email"
            placeholder="gsd@forgeworks.ai"
            value={email}
            onChange={handleEmailChange}
            className="w-full px-3 py-2.5 text-sm border border-divider dark:border-divider-dark rounded-lg bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition"
            disabled={loading}
            autoComplete="email"
          />
        </div>

        {message && (
          <div
            className={`mb-6 p-3 rounded-lg text-sm ${
              message.type === "success"
                ? "bg-green-light dark:bg-green-dark/20 text-green-dark dark:text-green-light"
                : "bg-red-light dark:bg-red-dark/20 text-red-dark dark:text-red-light"
            }`}
          >
            <p className="text-center">{message.text}</p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="py-2.5 px-6 rounded-lg text-sm font-medium border border-divider dark:border-divider-dark text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors"
          >
            {message?.type === "success" ? "Close" : "Cancel"}
          </button>
          {message?.type !== "success" && (
            <button
              onClick={handleSubmit}
              disabled={loading || !email}
              className="py-2.5 px-6 rounded-lg text-sm font-semibold bg-secondary hover:bg-secondary-hover text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordDialog;
